import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SubscriptionTab = () => {
  const [currentPlan] = useState({
    name: "Professional",
    price: 49,
    billing: "monthly",
    features: [
      "Unlimited projects",
      "HD video rendering",
      "50GB cloud storage",
      "Priority support",
      "Custom templates"
    ],
    nextBilling: "November 22, 2025"
  });

  const [usage] = useState({
    projects: { used: 23, limit: "Unlimited" },
    storage: { used: 32.5, limit: 50 },
    renders: { used: 156, limit: "Unlimited" }
  });

  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: 19,
      billing: "monthly",
      features: [
        "5 projects per month",
        "HD video rendering",
        "10GB cloud storage",
        "Email support"
      ],
      popular: false
    },
    {
      id: "professional",
      name: "Professional",
      price: 49,
      billing: "monthly",
      features: [
        "Unlimited projects",
        "HD video rendering",
        "50GB cloud storage",
        "Priority support",
        "Custom templates"
      ],
      popular: true
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: 99,
      billing: "monthly",
      features: [
        "Unlimited projects",
        "4K video rendering",
        "500GB cloud storage",
        "24/7 phone support",
        "Custom branding",
        "API access"
      ],
      popular: false
    }
  ];

  const billingHistory = [
    {
      id: 1,
      date: "October 22, 2025",
      amount: 49.00,
      plan: "Professional",
      status: "paid",
      invoice: "INV-2025-1022"
    },
    {
      id: 2,
      date: "September 22, 2025",
      amount: 49.00,
      plan: "Professional",
      status: "paid",
      invoice: "INV-2025-0922"
    },
    {
      id: 3,
      date: "August 22, 2025",
      amount: 49.00,
      plan: "Professional",
      status: "paid",
      invoice: "INV-2025-0822"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Current Plan */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Current Plan</h3>
            <p className="text-sm text-muted-foreground">Manage your subscription and billing</p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" iconName="Download">
              Download Invoice
            </Button>
            <Button variant="outline" iconName="CreditCard">
              Update Payment
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Crown" size={24} color="white" />
              </div>
              <div>
                <h4 className="text-xl font-semibold text-foreground">{currentPlan?.name} Plan</h4>
                <p className="text-sm text-muted-foreground">
                  ${currentPlan?.price}/{currentPlan?.billing} • Next billing: {currentPlan?.nextBilling}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {currentPlan?.features?.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success" />
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4">
              <h5 className="font-medium text-foreground mb-3">Usage This Month</h5>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Projects</span>
                    <span className="text-foreground">{usage?.projects?.used}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Storage</span>
                    <span className="text-foreground">{usage?.storage?.used}GB / {usage?.storage?.limit}GB</span>
                  </div>
                  <div className="w-full bg-border rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${(usage?.storage?.used / usage?.storage?.limit) * 100}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Video Renders</span>
                    <span className="text-foreground">{usage?.renders?.used}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Available Plans */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Available Plans</h3>
          <p className="text-sm text-muted-foreground">Choose the plan that best fits your needs</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans?.map((plan) => (
            <div 
              key={plan?.id}
              className={`relative border rounded-lg p-6 ${
                plan?.id === 'professional' ?'border-primary bg-primary/5' :'border-border bg-card'
              }`}
            >
              {plan?.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h4 className="text-lg font-semibold text-foreground mb-2">{plan?.name}</h4>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-foreground">${plan?.price}</span>
                  <span className="text-muted-foreground">/{plan?.billing}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan?.features?.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={plan?.id === 'professional' ? 'secondary' : 'default'}
                fullWidth
                disabled={plan?.id === 'professional'}
              >
                {plan?.id === 'professional' ? 'Current Plan' : 'Upgrade'}
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Billing History */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Billing History</h3>
            <p className="text-sm text-muted-foreground">View and download your past invoices</p>
          </div>
          <Button variant="outline" iconName="Download">
            Export All
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Plan</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory?.map((item) => (
                <tr key={item?.id} className="border-b border-border">
                  <td className="py-3 px-4 text-sm text-foreground">{item?.date}</td>
                  <td className="py-3 px-4 text-sm text-foreground">{item?.plan}</td>
                  <td className="py-3 px-4 text-sm text-foreground">${item?.amount?.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                      <Icon name="Check" size={12} className="mr-1" />
                      Paid
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <Button variant="ghost" size="sm" iconName="Download">
                      {item?.invoice}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTab;